import React, { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Box, Text } from "@dxp-feed/ui";
import OffersDetailSliderCard from "../JustForYouCards/OfferDetailsSliderCard";

const OffersDetailSlider = ({
  activeOffers,
  selectedIndex,
  setIsOpen,
  headingParam,
  isHash,
  openDialog,
  onVisibilityChange,
  addedToListOffers,
  posOfferData,
  setIsPanelOpen,
  setGroupOffers,
  setFeaturedOffer,
  isOpen,
  fromShoppingList,
}: {
  activeOffers: any[];
  selectedIndex: number;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  headingParam?: string;
  openDialog: any;
  onVisibilityChange?: any;
  isHash: boolean;
  addedToListOffers: any[];
  posOfferData: any;
  setIsPanelOpen: any;
  setGroupOffers: any;
  setFeaturedOffer: any;
  isOpen: boolean;
  fromShoppingList?: boolean;
}) => {
  const [bottomSheetEmblaRef, bottomSheetEmblaApi] = useEmblaCarousel({
    loop: false,
  });

  const [isAnimating, setIsAnimating] = useState(false);
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(selectedIndex);
  const totalOffers = activeOffers?.length || 0;

  const closeBottomSheet = () => {
    setIsAnimating(false);
    document.body.classList.remove("offer-slider-open");
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  //   // Reset drag state
  const resetDragState = useCallback(() => {
    setIsDragging(false);
    setDragStartY(null);
    setDragOffsetY(0);
  }, []);

  useEffect(() => {
    setTimeout(() => setIsAnimating(true), 10);
  }, []);

  useEffect(() => {
    if (!bottomSheetEmblaApi) return;
    bottomSheetEmblaApi.scrollTo(selectedIndex);
    const onSelect = () => {
      const selectedSlideIndex = bottomSheetEmblaApi.selectedScrollSnap();
      setCurrentIndex(selectedSlideIndex);
    };

    bottomSheetEmblaApi.on("select", onSelect);
    onSelect();

    return () => {
      bottomSheetEmblaApi.off("select", onSelect);
    };
  }, [bottomSheetEmblaApi, selectedIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setDragStartY(touch.clientY);
    setIsDragging(true);
    setDragOffsetY(0);
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (isDragging && dragStartY !== null) {
        const touch = e.touches[0];
        const offset = touch.clientY - dragStartY;
        setDragOffsetY(offset > 0 ? offset : 0);
      }
    },
    [isDragging, dragStartY]
  );

  const handleTouchEnd = useCallback(() => {
    if (dragOffsetY > 200) {
      closeBottomSheet();
    } else {
      setDragOffsetY(0);
    }
    resetDragState();
  }, [dragOffsetY, closeBottomSheet, resetDragState]);

  // Event listeners management
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
      window.addEventListener("touchcancel", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isDragging, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    if (isAnimating) {
      document.body.classList.add("offer-slider-open");
    }
    return () => {
      document.body.classList.remove("offer-slider-open");
    };
  }, [isAnimating]);

  if (!isOpen) return null;
  return (
    <>
      <Box
        as="div"
        className="fixed inset-0 z-50 flex items-end justify-center"
      >
        {/* Overlay */}
        <Box
          as="div"
          role="button"
          tabIndex={0}
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isAnimating ? "opacity-50" : "opacity-0"
          }`}
          onClick={closeBottomSheet}
        ></Box>

        {/* Bottom Sheet */}
        <Box
          as="div"
          className={`bg-white max-h-[99dvh] rounded-t-2xl pt-3 pb-0 px-0 w-full overflow-hidden relative z-50 transition-transform duration-300 ease-in-out ${
            isAnimating && dragOffsetY === 0 ? "translate-y-0" : ""
          } ${!isAnimating ? "translate-y-full" : ""}`}
          style={{
            transform:
              dragOffsetY > 0
                ? `translateY(${dragOffsetY}px)`
                : "translateY(0)",
            transition: isDragging ? "none" : "transform 0.3s ease",
          }}
        >
          <Box className="sticky" onTouchStart={handleTouchStart}>
            <Box
              as="div"
              className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab active:cursor-grabbing"
            />
            <Box
              as="div"
              className="border-b-[1px] border-grey600 mb-[8px] px-2"
            >
              <Text as="h2" className="text-xl font-bold mb-4 -mt-2">
                Offer Details
              </Text>
              <button
                className="absolute top-0 right-4 text-2xl"
                onClick={closeBottomSheet}
              >
                &times;
              </button>
            </Box>

            <Box
              as="div"
              className="flex items-center justify-center w-full mt-0 mb-0 ease-in-out"
            >
              <Box
                as="div"
                className="relative w-[140px] h-[32px] preserve-3d transition-transform duration-800 ease-in-out mx-auto flex items-center justify-center"
              >
                {[...Array(Math.min(totalOffers, 3))].map((_, dotPos) => {
                  let representedIndex: number;
                  if (totalOffers < 3) {
                    representedIndex = dotPos;
                  } else if (currentIndex === totalOffers - 1) {
                    representedIndex = totalOffers - 3 + dotPos;
                  } else if (currentIndex === 0) {
                    representedIndex = dotPos;
                  } else {
                    representedIndex = currentIndex + 1 - dotPos;
                  }
                  representedIndex = Math.max(
                    0,
                    Math.min(representedIndex, totalOffers - 1)
                  );

                  const isActive = representedIndex === currentIndex;

                  return (
                    <Box
                      as="button"
                      key={`dot-${representedIndex}`}
                      className={`h-[10px] mx-1 rounded-full transition-all duration-300 ${
                        isActive
                          ? "bg-brandPrimary opacity-100 w-[18px] scale-[1.25]"
                          : "bg-brandPrimary opacity-50 scale-[1] w-[10px]"
                      }`}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
          <Box
            as="div"
            ref={bottomSheetEmblaRef}
            className="embla emblaMobileOffers"
          >
            <Box as="div" className="embla__container flex">
              {activeOffers?.map((offer, index) => {
                const obj = addedToListOffers?.find(
                  (addedOffer: any) =>
                    addedOffer?.offer_id === offer?.offerId ||
                    addedOffer?.offerId === offer?.offerId
                );
                return (
                  <Box
                    as="div"
                    key={offer.regionalFlag + index}
                    className="embla__slide flex-shrink-0 w-full px-0"
                  >
                    <OffersDetailSliderCard
                      featuredCardData={offer}
                      isHash={isHash}
                      headingParam={headingParam}
                      openDialog={openDialog}
                      setIsOpen={setIsOpen}
                      onVisibilityChange={onVisibilityChange}
                      offerAddedInList={obj ? 1 : 0}
                      posOfferData={posOfferData}
                      setIsPanelOpen={setIsPanelOpen}
                      setGroupOffers={setGroupOffers}
                      setFeaturedOffer={setFeaturedOffer}
                      fromShoppingList={fromShoppingList}
                      closeBottomSheet={closeBottomSheet}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OffersDetailSlider;
